import React, { useState } from "react";
import PropTypes from "prop-types";
import { Check, Loader2, AlertTriangle, Clock, ChevronRight } from "lucide-react";
import { formatToolDisplayName } from "./formatToolDisplayName";
import { StepInspector, hasToolArguments } from "./StepInspector";

export const ActivityActionRow = ({ step, idx, t, isLive = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!step || typeof step !== "object") return null;

  const isRunning = step.status === "running";
  const isError = step.status === "error";
  const isTimeout = step.status === "timeout";

  const hasArgs = hasToolArguments(step.tool_args);
  const previewOutput = step.tool_output_preview || step.tool_output;
  const hasDetails = Boolean(hasArgs || previewOutput || (isError && step.error_message));

  const durationStr =
    typeof step.execution_time_ms === "number"
      ? `${step.execution_time_ms}ms`
      : typeof step.duration_ms === "number"
      ? `${step.duration_ms}ms`
      : null;

  const displayLabel = formatToolDisplayName(step, t, isRunning);

  const statusLabel = isRunning
    ? t ? t("assistant.statusRunning", "Running") : "Running"
    : isError
    ? t ? t("assistant.statusError", "Failed") : "Failed"
    : isTimeout
    ? t ? t("assistant.statusTimeout", "Timed out") : "Timed out"
    : t ? t("assistant.statusSuccess", "Completed") : "Completed";

  const inspectorId = `action-details-${step.tool_call_id || idx}`;

  return (
    <div className="elelem-activity-action-container">
      <button
        type="button"
        className={`elelem-activity-action-row ${isRunning ? "running" : "done"} ${isLive && isRunning ? "elelem-activity-shimmer" : ""} ${isExpanded ? "expanded" : ""} ${!hasDetails ? "no-details" : ""}`}
        onClick={() => hasDetails && setIsExpanded((prev) => !prev)}
        disabled={!hasDetails && !isRunning}
        aria-expanded={hasDetails ? isExpanded : undefined}
        aria-controls={hasDetails ? inspectorId : undefined}
        data-testid={`copilot-step-row-${step.tool_name || idx}`}
      >
        <span className="elelem-activity-action-left">
          {isRunning ? (
            <Loader2
              size={12}
              className="spin-icon elelem-activity-spinner me-2 flex-shrink-0"
              aria-label={statusLabel}
              role="status"
            />
          ) : isError ? (
            <AlertTriangle
              size={12}
              className="text-danger me-2 flex-shrink-0"
              aria-label={statusLabel}
              role="status"
            />
          ) : isTimeout ? (
            <Clock
              size={12}
              className="text-warning me-2 flex-shrink-0"
              aria-label={statusLabel}
              role="status"
            />
          ) : (
            <Check
              size={12}
              className="elelem-activity-check me-2 flex-shrink-0"
              aria-label={statusLabel}
              role="status"
            />
          )}
          <span className="elelem-activity-action-label">{displayLabel}</span>
        </span>
        <span className="elelem-activity-action-right d-flex align-items-center">
          {durationStr && !isExpanded && (
            <span className="elelem-activity-meta-duration text-muted small me-1">
              {durationStr}
            </span>
          )}
          {hasDetails && (
            <ChevronRight
              size={11}
              className={`elelem-activity-chevron ms-1 ${isExpanded ? "open" : ""}`}
              aria-hidden="true"
            />
          )}
        </span>
      </button>

      {isExpanded && hasDetails && <StepInspector step={step} t={t} id={inspectorId} />}
    </div>
  );
};

ActivityActionRow.propTypes = {
  step: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  t: PropTypes.func,
  isLive: PropTypes.bool,
};
